/**
 * Catalog of festival deadlines + registration access for admin visibility.
 * Code defaults live here / in iabf-registration-2027.js; live overrides in
 * Firestore `iabf-config-{year}/deadlines` and `…/registration`.
 */

import { festivalWeek } from './iabf-festival-dates'
import {
  REGISTRATION_YEAR,
  EARLY_ACCESS_OPENS_LABEL,
  DIETARY_ENTRY_DEADLINE,
  SHOWCASE_REGISTRATION_DEADLINE,
  COMPETITION_REGISTRATION_DEADLINE,
  COMPETITION_PIECE_CHANGE_DEADLINE,
  GUEST_REGISTRATION_DEADLINE,
  CARPOOL_REGISTRATION_DEADLINE,
  CARPOOL_CHANGE_DEADLINE,
  CANCEL_POLICY,
  isEarlyAccessTimeReached
} from './iabf-registration-2027'

/** @typedef {'registration'|'prep'|'festival'|'policy'} DeadlineCategory */

/**
 * @typedef {object} DeadlineDef
 * @property {string} key
 * @property {string} label
 * @property {string} description
 * @property {DeadlineCategory} category
 * @property {string|null} defaultIso YYYY-MM-DD or null = open / not set
 * @property {boolean} [editable] can override in Firestore
 * @property {boolean} [isOpenEnded] null means “open until set”
 * @property {string} [legacy2026] display only
 */

/** Runtime overrides loaded from Firestore (admin or app boot). */
let runtimeDeadlineOverrides = {}
let runtimeRegistrationAccess = null

export function setDeadlineOverrides (overrides) {
  runtimeDeadlineOverrides = overrides && typeof overrides === 'object' ? { ...overrides } : {}
}

export function getDeadlineOverrides () {
  return { ...runtimeDeadlineOverrides }
}

/**
 * @param {{ publicRegistrationOpen?: boolean, earlyAccessOpen?: boolean, publicOpensAt?: string|null, earlyAccessOpensAt?: string|null, notes?: string }|null} access
 */
export function setRegistrationAccessRuntime (access) {
  runtimeRegistrationAccess = access || null
}

export function getRegistrationAccessRuntime () {
  return runtimeRegistrationAccess
}

/**
 * @param {number} [year]
 * @returns {DeadlineDef[]}
 */
export function deadlineCatalog (year = REGISTRATION_YEAR) {
  const y = Number(year) || REGISTRATION_YEAR
  const week = festivalWeek(y)
  return [
    {
      key: 'earlyAccessOpen',
      label: 'Early registration (returners & canceled)',
      description: `Returners and canceled dancers with a held deposit. The switch opens and closes it. If you have never saved a choice, it auto-opens ${EARLY_ACCESS_OPENS_LABEL}.`,
      category: 'registration',
      defaultIso: null,
      editable: false,
      kind: 'switch'
    },
    {
      key: 'publicRegistrationOpen',
      label: 'Public registration open',
      description: 'When on, anyone can start a new registration. Flip this for your public open event. Off until you flip it — missing config stays closed.',
      category: 'registration',
      defaultIso: null,
      editable: false,
      kind: 'switch'
    },
    {
      key: 'publicOpensAt',
      label: 'Public open date (planned)',
      description: 'Optional calendar date for planning — does not auto-open; use the Public registration switch.',
      category: 'registration',
      defaultIso: null,
      editable: true,
      isOpenEnded: true,
      kind: 'date'
    },
    {
      key: 'earlyAccessOpensAt',
      label: 'Early access open date (planned)',
      description: 'Optional calendar date for planning — does not auto-open; use the Early registration switch.',
      category: 'registration',
      defaultIso: null,
      editable: true,
      isOpenEnded: true,
      kind: 'date'
    },
    {
      key: 'dietaryEntry',
      label: 'Dietary / catering self-entry closes',
      description: 'Dancers can no longer edit dietary preferences after this date (caterer counts).',
      category: 'prep',
      defaultIso: DIETARY_ENTRY_DEADLINE,
      editable: true,
      isOpenEnded: true,
      legacy2026: '2026-06-01',
      kind: 'date'
    },
    {
      key: 'guestRegistration',
      label: 'Guest meals & banquet registration closes',
      description: 'Guests / welcome dinner / banquet signups close.',
      category: 'prep',
      defaultIso: GUEST_REGISTRATION_DEADLINE,
      editable: true,
      isOpenEnded: true,
      legacy2026: '2026-06-01',
      kind: 'date'
    },
    {
      key: 'showcaseRegistration',
      label: 'Showcase solo registration closes',
      description: 'Last day to register a new showcase piece.',
      category: 'prep',
      defaultIso: SHOWCASE_REGISTRATION_DEADLINE,
      editable: true,
      isOpenEnded: true,
      legacy2026: '2026-06-01',
      kind: 'date'
    },
    {
      key: 'competitionRegistration',
      label: 'Competition registration closes',
      description: 'Last day to enter the competition.',
      category: 'prep',
      defaultIso: COMPETITION_REGISTRATION_DEADLINE,
      editable: true,
      isOpenEnded: true,
      legacy2026: '2026-06-01',
      kind: 'date'
    },
    {
      key: 'competitionPieceChange',
      label: 'Competition piece change closes',
      description: 'Last day dancers can switch which piece competes / re-do the survey.',
      category: 'prep',
      defaultIso: COMPETITION_PIECE_CHANGE_DEADLINE,
      editable: true,
      isOpenEnded: true,
      legacy2026: '2026-06-01',
      kind: 'date'
    },
    {
      key: 'carpoolRegistration',
      label: 'Airport carpool signup closes',
      description: 'Last day to join a carpool.',
      category: 'prep',
      defaultIso: CARPOOL_REGISTRATION_DEADLINE,
      editable: true,
      isOpenEnded: true,
      legacy2026: 'closed for season',
      kind: 'date'
    },
    {
      key: 'carpoolChange',
      label: 'Carpool charge & self-serve cancel',
      description: 'Dancers are charged and cannot self-serve cancel after this date. Shown on the carpool page.',
      category: 'prep',
      defaultIso: CARPOOL_CHANGE_DEADLINE || `${y}-07-01`,
      editable: true,
      isOpenEnded: true,
      legacy2026: '2026-07-01',
      kind: 'date'
    },
    {
      key: 'cancelBy',
      label: 'Cancellation / refund request by',
      description: 'Policy cancel-by date shown to dancers (partial refund rules).',
      category: 'policy',
      defaultIso: cancelByToIso(CANCEL_POLICY.cancelByLabel, y),
      editable: true,
      isOpenEnded: false,
      kind: 'date'
    },
    {
      key: 'guestRefund',
      label: 'Guest meal / banquet refunds end',
      description: 'Last day guest meal and banquet fees can be refunded. Shown on the dancer guest page.',
      category: 'policy',
      defaultIso: `${y}-05-31`,
      editable: true,
      isOpenEnded: true,
      legacy2026: '2026-05-31',
      kind: 'date'
    },
    {
      key: 'extraNightInvoiceDue',
      label: 'Extra-night invoice due',
      description: 'Due date on extra-night invoice links in the dancer editor. Clear if nights roll into installments with no separate invoice.',
      category: 'policy',
      defaultIso: `${y}-06-15`,
      editable: true,
      isOpenEnded: true,
      legacy2026: '2026-06-15',
      kind: 'date'
    },
    {
      key: 'festivalArrival',
      label: 'Festival arrival / check-in',
      description: 'First festival night check-in.',
      category: 'festival',
      defaultIso: week.arrivalIso,
      editable: false,
      kind: 'date'
    },
    {
      key: 'festivalDeparture',
      label: 'Festival departure / checkout',
      description: 'Last festival morning checkout.',
      category: 'festival',
      defaultIso: week.departureIso,
      editable: false,
      kind: 'date'
    }
  ]
}

function cancelByToIso (label, year) {
  // "May 6, 2027" → 2027-05-06
  const m = String(label || '').match(/([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})/)
  if (!m) {
    return null
  }
  const months = {
    january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
    july: 7, august: 8, september: 9, october: 10, november: 11, december: 12
  }
  const mo = months[m[1].toLowerCase()]
  if (!mo) {
    return null
  }
  const day = String(parseInt(m[2], 10)).padStart(2, '0')
  const y = m[3] || year
  return `${y}-${String(mo).padStart(2, '0')}-${day}`
}

/**
 * Effective ISO date for a deadline key (override wins over code default).
 * @param {string} key
 * @param {number} [year]
 * @returns {string|null}
 */
export function effectiveDeadlineIso (key, year = REGISTRATION_YEAR) {
  if (Object.prototype.hasOwnProperty.call(runtimeDeadlineOverrides, key)) {
    const v = runtimeDeadlineOverrides[key]
    if (v === '' || v === undefined) {
      return null
    }
    return v
  }
  const def = deadlineCatalog(year).find(d => d.key === key)
  return def ? def.defaultIso : null
}

/**
 * @param {string|null} iso
 * @returns {'past'|'today'|'upcoming'|'open'|'unknown'}
 */
export function deadlineStatus (iso) {
  if (!iso) {
    return 'open'
  }
  const d = String(iso).slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    return 'unknown'
  }
  const today = new Date()
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const target = new Date(d + 'T00:00:00')
  if (Number.isNaN(target.getTime())) {
    return 'unknown'
  }
  const diff = Math.round((target - t) / (24 * 60 * 60 * 1000))
  if (diff < 0) {
    return 'past'
  }
  if (diff === 0) {
    return 'today'
  }
  return 'upcoming'
}

export function formatDeadlineDisplay (iso) {
  if (!iso) {
    return 'Not set (open)'
  }
  const parts = String(iso).slice(0, 10).split('-')
  if (parts.length !== 3) {
    return iso
  }
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  const m = parseInt(parts[1], 10)
  const day = parseInt(parts[2], 10)
  const y = parts[0]
  if (!m || !day) {
    return iso
  }
  return `${months[m - 1]} ${day}, ${y}`
}

export function daysUntil (iso) {
  if (!iso) {
    return null
  }
  const d = String(iso).slice(0, 10)
  const target = new Date(d + 'T00:00:00')
  if (Number.isNaN(target.getTime())) {
    return null
  }
  const today = new Date()
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.round((target - t) / (24 * 60 * 60 * 1000))
}

export const DEADLINE_CATEGORIES = [
  { id: 'registration', label: 'Registration access' },
  { id: 'prep', label: 'Dancer prep deadlines' },
  { id: 'policy', label: 'Policies' },
  { id: 'festival', label: 'Festival week' }
]

/**
 * Resolve registration gate from a Firestore registration doc (or runtime).
 * Missing / unreadable doc → closed until scheduled early access, never public.
 * Once admin has saved the early-access switch, that value wins — unchecking
 * closes it even after the scheduled auto-open. Public stays off until flipped.
 * @param {object|null} doc
 * @param {Date} [now]
 * @returns {{ mode: 'public'|'early'|'closed', publicRegistrationOpen: boolean, earlyAccessOpen: boolean, scheduledEarlyAccess: boolean, publicOpensAt: string|null, earlyAccessOpensAt: string|null, notes: string }}
 */
export function earlyAccessOpenFromDoc (doc, scheduledEarlyAccess) {
  const d = doc && typeof doc === 'object' ? doc : {}
  if (Object.prototype.hasOwnProperty.call(d, 'earlyAccessOpen')) {
    return !!d.earlyAccessOpen
  }
  return !!scheduledEarlyAccess
}

export function resolveRegistrationAccess (doc, now = new Date()) {
  const d = doc && typeof doc === 'object' ? doc : {}
  const publicRegistrationOpen = !!d.publicRegistrationOpen
  const scheduledEarlyAccess = isEarlyAccessTimeReached(now)
  const earlyAccessOpen = earlyAccessOpenFromDoc(d, scheduledEarlyAccess)
  let mode = 'closed'
  if (publicRegistrationOpen) {
    mode = 'public'
  } else if (earlyAccessOpen) {
    mode = 'early'
  }
  return {
    mode,
    publicRegistrationOpen,
    earlyAccessOpen,
    scheduledEarlyAccess,
    publicOpensAt: d.publicOpensAt || null,
    earlyAccessOpensAt: d.earlyAccessOpensAt || null,
    notes: String(d.notes || '')
  }
}

/**
 * Apply deadline ISO overrides into the is*Closed helpers via registration module.
 * Keys map to registration-2027 deadline constants.
 */
export function deadlineKeyToClosedChecker () {
  return {
    dietaryEntry: 'dietary',
    showcaseRegistration: 'showcase',
    competitionRegistration: 'competition',
    competitionPieceChange: 'competitionPiece',
    guestRegistration: 'guest',
    carpoolRegistration: 'carpool',
    carpoolChange: 'carpoolChange'
  }
}
